import { useEffect, useState, useContext } from "react";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import AuthContext from "../../../../utils/secure-route/AuthContext";
import { useToastHandler } from "../../../common/ToastHandler";
import AssetRequestTable from "./AssetRequestTable";
import ReusableModal from "../../AppPAges/ManageSettings/ReusableModal";
import { ReusableInput } from "../../../common/ReusableComponent/ReusableInput";
import { RegularButton } from "../../../common/ReusableComponent/CustomButton";
import PaginatedSearchSelect from "../../../common/ReusableComponent/PaginatedSearchSelect/PaginatedSearchSelect";
import StatusPill from "../../../common/ReusableComponent/StatusPill";
import AssetTable from "../AssetsInventory/AssetTable";
import ActionButton from "../../../common/ReusableComponent/ActionButton";
import { FaCheck, FaTimes, FaBoxOpen } from "react-icons/fa";
import { ToastContainer } from "react-toastify";
const AssetRequestList = () => {
  const { GetPaginatedData, Patch, Post, Get, getAPI } = useApi();
  const { handleToast } = useToastHandler();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showAllocateModal, setShowAllocateModal] = useState(false);

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  const [formData, setFormData] = useState({
    asset_id: "",
    allocated_to: "",
    allocation_date: "",
  });

  const [selectedAssetName, setSelectedAssetName] = useState("");

  const [scannedBarcode, setScannedBarcode] = useState("");
  const [scannedAsset, setScannedAsset] = useState(null);

  const [allocateFromAnotherStore, setAllocateFromAnotherStore] = useState(false);
  const [showStoreSelectModal, setShowStoreSelectModal] = useState(false);

  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState("");

  const [storeAvailability, setStoreAvailability] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);

  const [assets, setAssets] = useState([]);

  const [selectedAsset, setSelectedAsset] = useState("");
  const [storeBarcodeInput, setStoreBarcodeInput] = useState("");

  /*--------------Fetch Store -------*/
  const getAllStoresOfUser = async () => {
    try {
      const res = await Get("getAllStoresOfUser");
      return res;
    } catch (err) {
      handleToast({ type: "ERROR", message: "Failed to fetch stores" });
      return null;
    }
  };
  const getStoreAssetAvailability = async ({
    assetRequestId,
    storeId,
  }) => {
    try {
      const params = new URLSearchParams();

      if (assetRequestId) {
        params.append("asset_request_id", assetRequestId);
      }

      if (storeId) {
        params.append("store_ids", storeId);
      }

      const url = `${getAPI("storeAssetAvailability")}?${params.toString()}`;

      const res = await Get(url);

      // Check if response is valid JSON
      if (typeof res === 'string' && res.includes('<!DOCTYPE')) {
        throw new Error('Invalid API response - received HTML instead of JSON');
      }

      return res;
    } catch (err) {
      console.error('Store asset availability error:', err);
      handleToast({
        type: "ERROR",
        message: "Failed to fetch store asset availability",
      });
      return null;
    }
  };


  const handleStoreBarcodeScann = (barcode) => {
    if (!barcode || !assets.length) return;

    const foundAsset = assets.find(
      (asset) => asset.barcode?.toLowerCase() === barcode.toLowerCase()
    );

    if (foundAsset) {
      setSelectedAsset(foundAsset.asset_id);
      handleToast({
        type: "SUCCESS",
        message: `Asset found: ${foundAsset.name}`,
      });
    } else {
      handleToast({
        type: "ERROR",
        message: "Asset not found in this store",
      });
    }

    setStoreBarcodeInput("");
  };

  const handleStoreChange = async (storeId) => {
    setSelectedStore(storeId);
    setStoreAvailability(null);
    setAssets([]);

    if (!selectedRequest?.id || !storeId) return;

    try {
      setCheckingAvailability(true);

      const res = await getStoreAssetAvailability({
        assetRequestId: selectedRequest.id,
        storeId: storeId,
      });

      if (!res || !Array.isArray(res)) {
        setStoreAvailability({ available: false, available_qty: 0 });
        setAssets([]);
        return;
      }

      const store = res[0];

      setStoreAvailability({
        available: store?.has_asset ?? false,
        available_qty: store?.available_asset_count ?? 0,
      });

      setAssets(Array.isArray(store?.assets) ? store.assets : []);

    } catch (err) {
      console.error('Store change error:', err);
      handleToast({
        type: "ERROR",
        message: "Failed to load store details",
      });
      setStoreAvailability({ available: false, available_qty: 0 });
      setAssets([]);
    } finally {
      setCheckingAvailability(false);
    }
  };


  /* ---------------- FETCH LIST ---------------- */
  const fetchAssetRequestList = async (page = 1, search = "") => {
    try {
      setLoading(true);

      const response = await GetPaginatedData("assetRequest", {
        page,
        search: search || undefined,
      });

      setData(response?.data || []);
      setTotalCount(response?.totalCount || 0);
      setCurrentPage(page);
    } catch {
      handleToast({ type: "ERROR", message: "Failed to fetch asset requests" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchAssetRequestList(currentPage, debouncedSearchTerm);
  }, [currentPage, debouncedSearchTerm, refreshTrigger]);

  useEffect(() => {
    if (!showAllocateModal && !showStoreSelectModal) {
      setAllocateFromAnotherStore(false);
    }
  }, [showAllocateModal, showStoreSelectModal]);

  /* ---------------- APPROVE ---------------- */
  const handleApprove = async (request) => {
    try {
      await Patch("assetRequest", request.id, {
        status: "approved",
        is_approved: true,
      });

      handleToast({ type: "SUCCESS", message: "Request approved successfully" });
      setRefreshTrigger((prev) => prev + 1);
    } catch {
      handleToast({ type: "ERROR", message: "Failed to approve request" });
    }
  };

  /* ---------------- REJECT ---------------- */
  const handleReject = (request) => {
    setSelectedRequest(request);
    setShowRejectModal(true);
  };

  const submitRejection = async () => {
    if (!rejectionReason.trim()) {
      handleToast({ type: "ERROR", message: "Rejection reason is required" });
      return;
    }

    try {
      await Patch("assetRequest", selectedRequest.id, {
        status: "rejected",
        is_approved: false,
        reason_for_rejection: rejectionReason.trim(),
      });

      handleToast({ type: "SUCCESS", message: "Request rejected successfully" });
      setShowRejectModal(false);
      setRejectionReason("");
      setSelectedRequest(null);
      setRefreshTrigger((prev) => prev + 1);
    } catch {
      handleToast({ type: "ERROR", message: "Failed to reject request" });
    }
  };

  /* ---------------- ALLOCATION ---------------- */
  // const openAllocation = (request) => {
  //   setSelectedRequest(request);
  //   setFormData({
  //     asset_id: "",
  //     allocated_to: Number(request.request_from),
  //     allocation_date: "",
  //   });
  //   setSelectedAssetName("");
  //   setShowAllocateModal(true);
  // };

  // const openAllocation = (request) => {
  //   const today = new Date().toISOString().split('T')[0];

  //   setSelectedRequest(request);
  //   setFormData({
  //     asset_id: "",
  //     allocated_to: Number(request.request_from),
  //     allocation_date: today,
  //   });
  //   setSelectedAssetName("");
  //   setScannedBarcode("");
  //   setScannedAsset(null);
  //   setShowAllocateModal(true);
  // };

  const openAllocation = async (request) => {
    const today = new Date().toISOString().split("T")[0];

    const storeRes = await getAllStoresOfUser();

    if (storeRes) {
      const allStores = [
        storeRes.first_store,
        ...(storeRes.sub_stores || []),
      ];

      setStores(allStores);
      setSelectedStore(storeRes.first_store?.id || "");
    }

    setSelectedRequest(request);
    setFormData({
      asset_id: "",
      allocated_to: Number(request.request_from),
      allocation_date: today,
    });

    setSelectedAssetName("");
    setScannedBarcode("");
    setScannedAsset(null);
    setShowAllocateModal(true);
  };

  const handleAllocationAsset = async () => {
    // Check if allocating from another store
    const isFromAnotherStore = selectedStore && selectedStore !== user.store;
    const assetId = isFromAnotherStore ? selectedAsset : formData.asset_id;

    if (!assetId || !formData.allocated_to) {
      handleToast({ type: "ERROR", message: "Asset is required" });
      return;
    }

    const selectedStoreObj = stores.find(s => s.id === selectedStore);

    const payload = {
      request_id: selectedRequest?.id,
      asset_id: assetId,
      store: isFromAnotherStore ? selectedStore : user.store,
      allocated_to: formData.allocated_to,
      allocated_to_name: selectedRequest?.request_from_name || "",
      allocated_by: user.user_id,
      allocated_by_name: user.user_name || "",
      allocation_date: formData.allocation_date,
      store_name: isFromAnotherStore ? (selectedStoreObj?.name || "") : user.store_name
    };

    try {
      setLoading(true);
      await Post("assetAllocation", payload);

      handleToast({ type: "SUCCESS", message: "Asset allocated successfully" });

      setShowAllocateModal(false);
      setShowStoreSelectModal(false);
      setFormData({ asset_id: "", allocated_to: "" });
      setSelectedRequest(null);
      setSelectedStore("");
      setSelectedAsset("");
      setRefreshTrigger((prev) => prev + 1);
    } catch {
      handleToast({ type: "ERROR", message: "Failed to allocate asset" });
    } finally {
      setLoading(false);
    }
  };

  const handleBarcodeScan = async (barcode) => {
    if (!barcode) return;

    try {
      setLoading(true);
      console.log("Searching for barcode:", barcode);

      const res = await GetPaginatedData("asset", {
        search: barcode,
      });

      console.log("API Response:", res);
      const asset = res?.data?.[0];

      if (!asset) {
        handleToast({
          type: "ERROR",
          message: `Asset not found for barcode: ${barcode}`,
        });
        return;
      }

      setScannedAsset(asset);

      setFormData(prev => ({
        ...prev,
        asset_id: asset.id,
      }));

      setSelectedAssetName(asset.asset_name || asset.name || "");

      handleToast({
        type: "SUCCESS",
        message: "Asset found successfully",
      });
    } catch (error) {
      console.error("Barcode scan error:", error);
      handleToast({
        type: "ERROR",
        message: "Failed to search barcode",
      });
    } finally {
      setLoading(false);
      setScannedBarcode("");
    }
  };

  const renderActions = (request) => {
    if (request.status === "pending") {
      return (
        <div style={{ display: "flex", gap: "8px" }}>
          <ActionButton
            onClick={() => handleApprove(request)}
            bgColor="#e9fdf3"
            textColor="#12b76a"
            borderColor="#a6f4c5"
            hoverBgColor="#d1fae5"
          >
            <FaCheck />
            Approve
          </ActionButton>

          <ActionButton
            onClick={() => handleReject(request)}
            bgColor="#fdecec"
            textColor="#e11d48"
            borderColor="#fda4af"
            hoverBgColor="#fee2e2"
          >
            <FaTimes />
            Reject
          </ActionButton>
        </div>
      );
    }

    if (request.status === "approved" && !request.is_asset_allocated) {
      return (
        <ActionButton
          onClick={() => openAllocation(request)}
          bgColor="#ecfeff"
          textColor="#0891b2"
          borderColor="#bae6fd"
          hoverBgColor="#cffafe"
        >
          <FaBoxOpen />
          Allocate
        </ActionButton>
      );
    }

    return "-";
  };

  const REQUEST_STATUS_PILL = {
    pending: {
      bg: "#fff3cd",
      text: "#856404",
      border: "#ffeeba",
    },
    approved: {
      bg: "#e9fdf3",
      text: "#12b76a",
      border: "#a6f4c5",
    },
    rejected: {
      bg: "#fdecec",
      text: "#e11d48",
      border: "#fda4af",
    },
    allocated: {
      bg: "#ecfeff",
      text: "#0891b2",
      border: "#bae6fd",
    },
  };

  //   const InsightRow = ({ label, value }) => (
  //   <div className="insight-row">
  //     <span className="insight-label">{label}</span>
  //     <span className="insight-value">{value}</span>
  //   </div>
  // );

  const columns = [
    { header: "Category", field: "category_name" },
    { header: "Description", field: "description" },
    { header: "Requested From", field: "request_from_name" },
    { header: "Requested To", field: "request_to_name" },
    { header: "Allocated From Store",field:"allocation_store_name"},
    {
      header: "Status",
      render: (row) => {
        let status = row.status;

        if (row.status === "approved" && row.is_asset_allocated) {
          status = "allocated";
        }

        const cfg =
          REQUEST_STATUS_PILL[status] || REQUEST_STATUS_PILL.pending;

        return (
          <StatusPill
            label={status}
            bgColor={cfg.bg}
            textColor={cfg.text}
            borderColor={cfg.border}
          />
        );
      },
    }
    ,
    {
      header: "Actions",
      field: "actions",
      render: (row) => renderActions(row),
    },
  ];

  return (
    <div className="responsive-box">
      <div className="inventory-header">
        <div className="inventory-title">Asset Request List</div>
      </div>

      {/* <AssetRequestTable
        data={data}
        columns={columns}
        loading={loading}
        currentPage={currentPage}
        totalCount={totalCount}
        onPageChange={(page) => fetchAssetRequestList(page, searchTerm)}
        searchTerm={searchTerm}
        onSearch={(term) => {
          setSearchTerm(term);
          setCurrentPage(1);
        }}
      /> */}

      <AssetTable
        data={data}
        columns={columns}
        loading={loading}
        showFooter={true}
        currentPage={currentPage}
        totalCount={totalCount}
        onPageChange={(page) => fetchAssetRequestList(page, debouncedSearchTerm)}
        searchTerm={searchTerm}
        onSearch={(term) => {
          setSearchTerm(term);
          setCurrentPage(1);
        }}
      />
      {/* -------- Allocation Modal -------- */}

      <ReusableModal
        title="Allocate Asset"
        show={showAllocateModal}
        handleClose={() => {
          setShowAllocateModal(false);
          setAllocateFromAnotherStore(false);
        }}
        width="800px"
        height="480px"
      >
        <div
          className="p-3"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "20px",
            height: "100%",
            boxSizing: "border-box",
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ marginTop: "0px", paddingBottom: "5px" }}>
              <ReusableInput
                type="text"
                label="Requested Category"
                value={selectedRequest?.category_name || ""}
                disabled
                style={{ width: "280px" }}
              />
            </div>
            <div className="mt-3">

              <ReusableInput
                type="text"
                label="Scan Barcode"
                value={scannedBarcode}
                onChange={(e) => setScannedBarcode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === "Tab") {
                    e.preventDefault();
                    handleBarcodeScan(scannedBarcode.trim());
                  }
                }}
                style={{ width: "280px" }}
              />
            </div>

            <div style={{ marginTop: "20px", paddingBottom: "20px" }}>
              <PaginatedSearchSelect
                api="asset"
                GetPaginatedData={GetPaginatedData}
                getOptionLabel={(opt) => opt?.asset_name || opt?.name}
                onSelect={(opt) => {
                  setFormData(prev => ({
                    ...prev,
                    asset_id: opt?.id || "",
                  }));
                  setSelectedAssetName(opt?.asset_name || opt?.name || "");
                  setScannedAsset(opt);
                }}
                value={
                  formData.asset_id
                    ? { id: formData.asset_id, asset_name: selectedAssetName }
                    : null
                }
                placeholder="Select Asset *"
                required
                style={{ width: "280px" }}
              />
            </div>

            <div style={{ marginTop: "10px" }}>
              <ReusableInput
                type="date"
                label="Allocation Date"
                value={formData.allocation_date}
                onChange={(e) =>
                  setFormData(prev => ({
                    ...prev,
                    allocation_date: e.target.value,
                  }))
                }
                required
                style={{ width: "280px" }}
              />
            </div>
            {stores.length > 1 && (
              <div className="p-3">
                <div className="interstore-card">
                  <p style={{ fontWeight: 600, margin: 0 }}>
                    Allocate from another store?
                  </p>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={allocateFromAnotherStore}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setAllocateFromAnotherStore(checked);
                        if (checked) {
                          setShowAllocateModal(false);
                          setShowStoreSelectModal(true);
                        }
                      }}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
              </div>
            )}
            <div className="d-flex justify-content-end gap-2 mt-4">
              <RegularButton
                text="Allocate"
                backgroundColor="#282e26"
                onClick={handleAllocationAsset}
              />
            </div>
          </div>

          <div
            style={{
              background: "linear-gradient(135deg, #f9fafb, #ffffff)",
              borderRadius: "10px",
              padding: "16px",
              boxShadow: "0 6px 20px rgba(0,0,0,0.10)",
              height: "100%",
              boxSizing: "border-box",
            }}
          >

            <h6
              style={{
                marginBottom: "12px",
                fontWeight: 600,
                color: "#333",
              }}
            >
              Asset Details
            </h6>

            {scannedAsset ? (
              <div style={{ fontSize: "14px", color: "#444" }}>
                {[
                  ["Asset", scannedAsset.name],
                  ["Barcode", scannedAsset.barcode],
                  ["Category", scannedAsset.category_name],
                  ["Status", scannedAsset.status],
                  ["Warranty", scannedAsset.warranty],
                  [
                    "Warranty Period",
                    `${scannedAsset.warranty_start_date} → ${scannedAsset.warranty_end_date}`,
                  ],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "8px 0",
                      borderBottom: "1px dashed #ddd",
                    }}
                  >
                    <span style={{ fontWeight: 500, color: "#555" }}>
                      {label}
                    </span>
                    <span style={{ color: "#222" }}>{value || "-"}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#888",
                  fontSize: "14px",
                  borderRadius: "8px",
                }}
              >
                Scan barcode to preview asset details
              </div>
            )}
          </div>
        </div>
      </ReusableModal>
      <ReusableModal
        title="Select Store"
        show={showStoreSelectModal}
        handleClose={() => {
          setShowStoreSelectModal(false);
          setAllocateFromAnotherStore(false);
          setStoreBarcodeInput("");
        }}
        width="420px"
      >
        <div className="p-3">
          <label style={{ fontWeight: 600 }}>Select Store</label>

          <select
            value={selectedStore}
            onChange={(e) => handleStoreChange(e.target.value)}
            style={{
              width: "100%",
              height: "38px",
              marginTop: "8px",
            }}
          >
            {stores.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          {checkingAvailability && (
            <p style={{ marginTop: "10px", fontSize: "13px" }}>
              Checking availability...
            </p>
          )}

          {storeAvailability && (
            <div
              style={{
                marginTop: "12px",
                padding: "10px",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                fontSize: "13px",
              }}
            >
              <div>
                <strong>Available:</strong>{" "}
                {storeAvailability.available ? "Yes" : "No"}
              </div>

              <div>
                <strong>Available Qty:</strong>{" "}
                {storeAvailability.available_qty}
              </div>

              {Array.isArray(storeAvailability?.assets) && storeAvailability.assets.length > 0 && (
                <div style={{ marginTop: "6px" }}>
                  <strong>Assets:</strong>
                  <ul>
                    {storeAvailability.assets.map(asset => (
                      <li key={asset.asset_id}>
                        {asset.name} ({asset.barcode})
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {Array.isArray(assets) && assets.length > 0 && (
            <>
              <div style={{ marginTop: "20px" }}>
                <ReusableInput
                  type="text"
                  label="Scan Barcode"
                  value={storeBarcodeInput}
                  onChange={(e) => setStoreBarcodeInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === "Tab") {
                      e.preventDefault();
                      handleStoreBarcodeScann(storeBarcodeInput.trim());
                    }
                  }}
                  style={{ width: "100%" }}
                />
              </div>

              <div style={{ marginTop: "10px" }}>
                <label style={{ fontSize: "13px" }}>
                  Asset
                </label>

                <select
                  value={selectedAsset}
                  onChange={(e) => setSelectedAsset(e.target.value)}
                  style={{
                    width: "100%",
                    marginTop: "4px",
                    padding: "6px",
                    fontSize: "13px",
                  }}
                >
                  <option value="">Select Asset</option>
                  {assets.map(asset => (
                    <option key={asset.asset_id} value={asset.asset_id}>
                      {asset.name} ({asset.barcode})
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          <div className="d-flex justify-content-end mt-4">
            <RegularButton
              text="Allocate"
              backgroundColor="#282e26"
              onClick={() => {
                handleAllocationAsset();
                setShowStoreSelectModal(false);
              }}
            />
          </div>
        </div>
      </ReusableModal>
      {/* -------- Reject Modal -------- */}
      <ReusableModal
        title="Reject Request"
        show={showRejectModal}
        handleClose={() => setShowRejectModal(false)}
        width="400px"
        height="300px"
      >
        <div className="p-3">
          <ReusableInput
            type="textarea"
            label="Reason for Rejection"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            rows={4}
            required
          />

          <div className="d-flex justify-content-end mt-3">
            <RegularButton
              text="Reject"
              onClick={submitRejection}
              backgroundColor="#282e26"
            />
          </div>
        </div>
      </ReusableModal>
      <ToastContainer />
    </div>
  );
};

export default AssetRequestList;
